import {
  Divider,
  Form,
  NumericFormatField,
  SelectField,
  SubmitButton,
  Text,
  TextAreaInputField,
} from '@/components';
import { useProject, useUpdateRemark } from '@/features/project/_hooks/project';
import { useForm, useId } from '@/hooks';
import { useMessage } from '@/providers';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BelanjaProgramModel,
  type IndonesiaData,
} from '../../../_models/indonesia-data';
import { useProjectDetailDrawerState } from '../../_components/ProjectDrawerStateProvider';

type FormValues = NonNullable<IndonesiaData['belanjaProgram']>[number];

type Props = {
  fiscalYear: number;
  initialValues?: Partial<FormValues>;
  isEditing?: boolean;
  mode?: 'Biaya' | 'Belanja';
};

export const ExpenditureItemForm: FC<Props> = ({
  fiscalYear,
  initialValues,
  isEditing,
  mode,
}) => {
  const { setDrawerState } = useProjectDetailDrawerState();
  const { openMessage } = useMessage();
  const { mutateAsync: updateRemarkAsync } = useUpdateRemark();
  const { t } = useTranslation();
  const { id } = useId();
  const { data: project } = useProject(id);
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
  } = useForm<FormValues>({
    defaultValues: {
      ...initialValues,
      id: initialValues ? initialValues.id : crypto.randomUUID(),
    },
    resolver: zodResolver(BelanjaProgramModel),
  });

  const hasChild = project?.indonesiaData?.belanjaProgram?.some(
    (v) => initialValues?.id && v.parentId === initialValues?.id,
  );

  const parentBelanjaProgram = project?.indonesiaData?.belanjaProgram?.flatMap(
    (v) => {
      return v.parentId || v.id === initialValues?.id
        ? []
        : [
            {
              label: v.name,
              value: v.id,
            },
          ];
    },
  );

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(async (values) => {
        const indonesiaData = project?.indonesiaData;
        if (!indonesiaData) {
          return;
        }

        const currentYearTotal =
          (values.currentYearAnggaranAPBD ?? 0) +
          (values.currentYearAnggaranLainnya ?? 0);
        const previousYearTotal =
          (values.previousYearAnggaranAPBD ?? 0) +
          (values.previousYearAnggaranLainnya ?? 0);

        const currentYearTargets = [
          currentYearTotal,
          values.currentYearRealisasi,
        ];
        const previousYearTargets = [
          previousYearTotal,
          values.previousYearRealisasi,
        ];

        const calculatedValues = {
          ...values,
          currentYearRate: currentYearTargets.every((v) => v)
            ? ((values.currentYearRealisasi ?? 0) / currentYearTotal) * 100
            : null,
          previousYearRate: previousYearTargets.every((v) => v)
            ? ((values.previousYearRealisasi ?? 0) / previousYearTotal) * 100
            : null,
        };
        const belanjaProgram = indonesiaData.belanjaProgram ?? [];
        try {
          await updateRemarkAsync({
            id,
            indonesiaData: {
              ...indonesiaData,
              belanjaProgram: isEditing
                ? belanjaProgram.map((v) =>
                    v.id === calculatedValues.id ? calculatedValues : v,
                  )
                : [
                    ...belanjaProgram,
                    {
                      ...calculatedValues,
                      createdAt: new Date().toISOString(),
                    },
                  ],
            },
          });
          openMessage(
            'success',
            isEditing ? t('message-update-completed') : t('add-completed'),
          );
          setDrawerState({ open: false });
        } catch {
          openMessage(
            'error',
            isEditing ? t('message-update-failed') : t('message-update-failed'),
          );
        }
      })}
    >
      <SelectField
        name="parentId"
        control={control}
        options={parentBelanjaProgram}
        label={t('orang-tua')}
        errors={errors}
        disabled={hasChild}
      />
      <NumericFormatField
        name="childIndex"
        control={control}
        label={t('indeks-anak')}
        min={0}
        errors={errors}
        disabled={hasChild}
      />

      <Divider />
      <TextAreaInputField
        name="name"
        label={mode === 'Biaya' ? t('biaya') : t('uraian-belanja-program')}
        control={control}
        errors={errors}
      />
      <Text variant="h4" text={fiscalYear} />

      <NumericFormatField
        name="currentYearAnggaranAPBD"
        control={control}
        label={mode === 'Biaya' ? t('org') : t('finalized-amount')}
        errors={errors}
      />
      <NumericFormatField
        name="currentYearAnggaranLainnya"
        control={control}
        label={mode === 'Biaya' ? t('rp') : t('next-request-amount')}
        errors={errors}
        disabled={false}
      />
      <NumericFormatField
        name="currentYearRealisasi"
        control={control}
        label={t('execution-amount')}
        errors={errors}
      />
      <Divider />

      <Text variant="h4" text={fiscalYear - 1} />

      <NumericFormatField
        name="previousYearAnggaranAPBD"
        control={control}
        label={mode === 'Biaya' ? t('org') : t('finalized-amount')}
        errors={errors}
      />
      <NumericFormatField
        name="previousYearAnggaranLainnya"
        control={control}
        label={mode === 'Biaya' ? t('rp') : t('next-request-amount')}
        errors={errors}
        disabled={false}
      />
      <NumericFormatField
        name="previousYearRealisasi"
        control={control}
        label={t('execution-amount')}
        errors={errors}
      />

      <SubmitButton loading={isSubmitting} disabled={!isDirty} />
    </Form>
  );
};
